import {
  normalisePlate,
  vehicleKey,
  type Alert,
  type AlertRule,
  type Device,
  type EventRecord,
  type NamedVehicle,
  type PlateDetection,
  type Reading,
  type Site,
  type Tenant,
  type TenantUser,
  type UnifiIntegration,
} from '@iot-dashboard/shared';

/** Deterministic sample data so the UI can be explored without AWS. */

export const DEMO_TENANT_IDS = ['acme', 'harbour', 'northwind'] as const;

const iso = (minutesAgo: number, now = Date.now()) => new Date(now - minutesAgo * 60_000).toISOString();

export function demoTenants(now = Date.now()): Tenant[] {
  return [
    {
      tenantId: 'acme',
      name: 'Acme Logistics',
      slug: 'acme-logistics',
      status: 'active',
      contactName: 'Priya Natarajan',
      contactEmail: 'priya@acme-logistics.example',
      timezone: 'Australia/Melbourne',
      tags: ['logistics', 'vic'],
      notes: 'Cold chain warehouse. Freezer alarms go to the after-hours line.',
      createdAt: iso(60 * 24 * 120, now),
      updatedAt: iso(60 * 24 * 3, now),
      createdBy: 'ops@msp.example',
    },
    {
      tenantId: 'harbour',
      name: 'Harbour Medical Centre',
      slug: 'harbour-medical',
      status: 'active',
      contactName: 'Dr. Liam Chen',
      contactEmail: 'admin@harbourmedical.example',
      timezone: 'Australia/Sydney',
      tags: ['healthcare', 'nsw'],
      createdAt: iso(60 * 24 * 45, now),
      updatedAt: iso(60 * 24 * 1, now),
      createdBy: 'ops@msp.example',
    },
    {
      tenantId: 'northwind',
      name: 'Northwind Retail',
      slug: 'northwind-retail',
      status: 'suspended',
      contactEmail: 'it@northwind.example',
      tags: ['retail'],
      createdAt: iso(60 * 24 * 200, now),
      updatedAt: iso(60 * 24 * 10, now),
      createdBy: 'ops@msp.example',
    },
  ];
}

export function demoSites(now = Date.now()): Site[] {
  const base = { source: 'unifi-protect' as const, createdAt: iso(60 * 24 * 100, now), updatedAt: iso(4, now) };
  return [
    { ...base, tenantId: 'acme', siteId: 'console-acme-hq', consoleId: 'console-acme-hq', name: 'Acme HQ – Melbourne', ipAddress: '203.0.113.10', firmwareVersion: '5.0.12', protectVersion: '6.1.80', status: 'online', lastSyncAt: iso(4, now), deviceCount: 7, location: 'Tullamarine, VIC' },
    { ...base, tenantId: 'acme', siteId: 'console-acme-dc', consoleId: 'console-acme-dc', name: 'Acme DC – Sydney', ipAddress: '198.51.100.22', firmwareVersion: '5.0.12', protectVersion: '6.1.80', status: 'online', lastSyncAt: iso(4, now), deviceCount: 3, location: 'Eastern Creek, NSW' },
    { ...base, tenantId: 'harbour', siteId: 'console-harbour', consoleId: 'console-harbour', name: 'Harbour Medical – Clinic', ipAddress: '192.0.2.44', firmwareVersion: '5.0.10', protectVersion: '6.1.75', status: 'online', lastSyncAt: iso(2, now), deviceCount: 4, location: 'Manly, NSW' },
  ];
}

interface DeviceSeed {
  tenantId: string;
  siteId: string;
  deviceId: string;
  name: string;
  model: string;
  kind: Device['kind'];
  status: Device['status'];
  mountType?: string;
  metrics: Device['metrics'];
  timestamps?: Device['timestamps'];
  lastSeenMin: number;
  battery?: number;
  tags?: string[];
  notes?: string;
  connectionState?: string;
  offlineSinceMin?: number;
  activeAlertCount?: number;
  camera?: Device['camera'];
}

const seeds: DeviceSeed[] = [
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'env-serverroom', name: 'Server room', model: 'Environmental sensor', kind: 'environmental', status: 'online', mountType: 'none', metrics: { temperature: 22.4, humidity: 41.2, light: 312, batteryPercentage: 88, batteryLow: false, leakDetected: false, tamperDetected: false, signalQuality: 82, signalStrength: -58 }, lastSeenMin: 1, tags: ['critical', 'it'] },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'env-coldstore', name: 'Cold store 1', model: 'Environmental sensor', kind: 'environmental', status: 'alerting', mountType: 'none', metrics: { temperature: 9.6, humidity: 71.5, light: 4, batteryPercentage: 64, batteryLow: false, leakDetected: false, tamperDetected: false, signalQuality: 61, signalStrength: -70 }, lastSeenMin: 2, tags: ['cold-chain'], notes: 'Set point 4 °C. Compressor serviced March 2026.', activeAlertCount: 1 },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'env-warehouse', name: 'Warehouse sump', model: 'Environmental sensor', kind: 'environmental', status: 'alerting', mountType: 'leak', metrics: { temperature: 18.9, humidity: 66.0, light: 120, batteryPercentage: 71, batteryLow: false, leakDetected: true, tamperDetected: false, signalQuality: 74, signalStrength: -63 }, timestamps: { leakDetectedAt: iso(6) }, lastSeenMin: 1, tags: ['cold-chain'], activeAlertCount: 1 },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'entry-dock', name: 'Loading dock door 2', model: 'Entry sensor', kind: 'entry', status: 'online', mountType: 'door', metrics: { isOpened: true, batteryPercentage: 93, batteryLow: false, tamperDetected: false, signalQuality: 88, signalStrength: -55 }, timestamps: { openStatusChangedAt: iso(4) }, lastSeenMin: 1, tags: ['dock'] },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'entry-front', name: 'Front entrance', model: 'Entry sensor', kind: 'entry', status: 'alerting', mountType: 'door', metrics: { isOpened: false, batteryPercentage: 12, batteryLow: true, tamperDetected: false, signalQuality: 79, signalStrength: -60 }, timestamps: { openStatusChangedAt: iso(38) }, lastSeenMin: 3, activeAlertCount: 1 },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'motion-office', name: 'Office corridor', model: 'Motion sensor', kind: 'motion', status: 'online', metrics: { motionDetected: false, batteryPercentage: 77, batteryLow: false, tamperDetected: false, signalQuality: 85, signalStrength: -57 }, timestamps: { motionDetectedAt: iso(22) }, lastSeenMin: 2 },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'gateway-hq', name: 'Sensor gateway – HQ', model: 'Sensor gateway', kind: 'gateway', status: 'online', metrics: {}, lastSeenMin: 1, tags: ['infrastructure'] },
  { tenantId: 'acme', siteId: 'console-acme-dc', deviceId: 'relay-generator', name: 'Generator run signal', model: 'Relay', kind: 'relay', status: 'online', metrics: { batteryPercentage: 100, batteryLow: false, tamperDetected: false, signalQuality: 90, signalStrength: -52 }, lastSeenMin: 1, tags: ['power'] },
  { tenantId: 'acme', siteId: 'console-acme-dc', deviceId: 'env-archive', name: 'Archive room', model: 'Environmental sensor', kind: 'environmental', status: 'offline', mountType: 'none', metrics: { temperature: 24.1, humidity: 48.0, light: 0, batteryPercentage: 55, batteryLow: false, leakDetected: false, tamperDetected: false }, lastSeenMin: 95, connectionState: 'DISCONNECTED', offlineSinceMin: 95, activeAlertCount: 1 },
  { tenantId: 'acme', siteId: 'console-acme-dc', deviceId: 'env-old', name: 'Old mezzanine sensor', model: 'Environmental sensor', kind: 'environmental', status: 'dormant', mountType: 'none', metrics: { temperature: 19.0, humidity: 50.0, light: 30, batteryPercentage: 3, batteryLow: true }, lastSeenMin: 60 * 24 * 21, connectionState: 'DISCONNECTED', offlineSinceMin: 60 * 24 * 21 },
  { tenantId: 'harbour', siteId: 'console-harbour', deviceId: 'env-vaccine', name: 'Vaccine fridge', model: 'Environmental sensor', kind: 'environmental', status: 'online', mountType: 'none', metrics: { temperature: 4.3, humidity: 35.8, light: 2, batteryPercentage: 91, batteryLow: false, leakDetected: false, tamperDetected: false, signalQuality: 87, signalStrength: -56 }, lastSeenMin: 1, tags: ['vaccines'] },
  { tenantId: 'harbour', siteId: 'console-harbour', deviceId: 'env-drugsafe', name: 'Drug safe', model: 'Environmental sensor', kind: 'environmental', status: 'online', mountType: 'none', metrics: { temperature: 21.7, humidity: 44.0, light: 1, batteryPercentage: 83, batteryLow: false, leakDetected: false, tamperDetected: false, signalQuality: 80, signalStrength: -59 }, lastSeenMin: 2 },
  { tenantId: 'harbour', siteId: 'console-harbour', deviceId: 'entry-drugsafe', name: 'Drug safe door', model: 'Entry sensor', kind: 'entry', status: 'online', mountType: 'door', metrics: { isOpened: false, batteryPercentage: 96, batteryLow: false, tamperDetected: false, signalQuality: 84, signalStrength: -58 }, timestamps: { openStatusChangedAt: iso(140) }, lastSeenMin: 1 },
  { tenantId: 'harbour', siteId: 'console-harbour', deviceId: 'motion-afterhours', name: 'Reception after-hours', model: 'Motion sensor', kind: 'motion', status: 'online', metrics: { motionDetected: true, batteryPercentage: 69, batteryLow: false, tamperDetected: false, signalQuality: 76, signalStrength: -62 }, timestamps: { motionDetectedAt: iso(1) }, lastSeenMin: 1 },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'cam-dock', name: 'Loading dock', model: 'Bullet camera', kind: 'camera', status: 'online', metrics: {}, lastSeenMin: 1, tags: ['dock'], camera: { videoMode: 'default', hdrMode: 'auto', hasHdr: true, hasMic: true, hasSpeaker: false, supportsFullHdSnapshot: true, smartDetectTypes: ['person', 'vehicle'] } },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'doorbell-reception', name: 'Reception doorbell', model: 'Doorbell', kind: 'doorbell', status: 'online', metrics: {}, lastSeenMin: 1, camera: { videoMode: 'default', hasMic: true, hasSpeaker: true, micEnabled: true, hasPackageCamera: true, supportsFullHdSnapshot: true, smartDetectTypes: ['person', 'package'], lcdMessage: 'Deliveries to side door' } },
  { tenantId: 'acme', siteId: 'console-acme-hq', deviceId: 'cam-coldstore', name: 'Cold store aisle', model: 'Turret camera', kind: 'camera', status: 'online', metrics: {}, lastSeenMin: 2, tags: ['cold-chain'], camera: { videoMode: 'default', hasHdr: true, hasMic: true, smartDetectTypes: ['person'] } },
  { tenantId: 'acme', siteId: 'console-acme-dc', deviceId: 'cam-carpark', name: 'Car park', model: 'Bullet camera', kind: 'camera', status: 'offline', metrics: {}, lastSeenMin: 140, connectionState: 'DISCONNECTED', offlineSinceMin: 140, activeAlertCount: 1, camera: { videoMode: 'default', hasHdr: true, smartDetectTypes: ['person', 'vehicle'] } },
  { tenantId: 'harbour', siteId: 'console-harbour', deviceId: 'cam-dispensary', name: 'Dispensary counter', model: 'Dome camera', kind: 'camera', status: 'online', metrics: {}, lastSeenMin: 1, tags: ['compliance'], camera: { videoMode: 'default', hasMic: true, hasSpeaker: true, supportsFullHdSnapshot: true, smartDetectTypes: ['person'] } },
];

export function demoDevices(now = Date.now()): Device[] {
  return seeds.map((s) => ({
    tenantId: s.tenantId,
    siteId: s.siteId,
    deviceId: s.deviceId,
    name: s.name,
    model: s.model,
    kind: s.kind,
    vendor: 'ubiquiti',
    source: 'unifi-protect',
    mac: macFor(s.deviceId),
    firmwareVersion: '1.4.2',
    hardwareRevision: 'rev2',
    mountType: s.mountType,
    status: s.status,
    connectionState: s.connectionState ?? 'CONNECTED',
    lastSeenAt: iso(s.lastSeenMin, now),
    lastSyncAt: iso(Math.min(s.lastSeenMin, 4), now),
    upSince: iso(60 * 24 * 40, now),
    offlineSince: s.offlineSinceMin !== undefined ? iso(s.offlineSinceMin, now) : undefined,
    metrics: s.metrics,
    ...(s.camera ? { camera: s.camera } : {}),
    timestamps: s.timestamps ?? {},
    activeAlertCount: s.activeAlertCount ?? 0,
    tags: s.tags ?? [],
    notes: s.notes,
    createdAt: iso(60 * 24 * 40, now),
    updatedAt: iso(4, now),
  }));
}

function macFor(id: string): string {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const hex = h.toString(16).padStart(8, '0').toUpperCase();
  return `F4E2${hex}`.slice(0, 12);
}

export function demoRules(tenantId: string, now = Date.now()): AlertRule[] {
  const base = { tenantId, enabled: true, deviceIds: [] as string[], siteIds: [] as string[], notifyEmails: [] as string[], createdAt: iso(60 * 24 * 100, now), updatedAt: iso(60 * 24 * 100, now) };
  const rules: AlertRule[] = [
    { ...base, ruleId: 'builtin-leak', name: 'Water leak detected', kind: 'leak', severity: 'critical', builtIn: true },
    { ...base, ruleId: 'builtin-battery', name: 'Battery low', kind: 'battery-low', severity: 'warning', threshold: 20, builtIn: true },
    { ...base, ruleId: 'builtin-offline', name: 'Sensor offline', kind: 'offline', severity: 'warning', durationMinutes: 15, builtIn: true },
    { ...base, ruleId: 'builtin-tamper', name: 'Tampering detected', kind: 'tamper', severity: 'warning', builtIn: true },
  ];
  if (tenantId === 'acme') {
    rules.push({ ...base, ruleId: 'rule-coldstore-temp', name: 'Cold store above 8 °C', kind: 'threshold', metric: 'temperature', operator: 'gt', threshold: 8, severity: 'critical', deviceIds: ['env-coldstore'], notifyEmails: ['ops@acme-logistics.example'], builtIn: false });
    rules.push({ ...base, ruleId: 'rule-dock-open', name: 'Dock door open > 30 min', kind: 'door-open', durationMinutes: 30, severity: 'warning', deviceIds: ['entry-dock'], builtIn: false });
  }
  if (tenantId === 'harbour') {
    rules.push({ ...base, ruleId: 'rule-vaccine-high', name: 'Vaccine fridge above 8 °C', kind: 'threshold', metric: 'temperature', operator: 'gt', threshold: 8, severity: 'critical', deviceIds: ['env-vaccine'], builtIn: false });
    rules.push({ ...base, ruleId: 'rule-vaccine-low', name: 'Vaccine fridge below 2 °C', kind: 'threshold', metric: 'temperature', operator: 'lt', threshold: 2, severity: 'critical', deviceIds: ['env-vaccine'], builtIn: false });
  }
  return rules;
}

export function demoAlerts(now = Date.now()): Alert[] {
  const mk = (a: Partial<Alert> & Pick<Alert, 'alertId' | 'tenantId' | 'ruleId' | 'ruleName' | 'kind' | 'deviceId' | 'deviceName' | 'severity' | 'message' | 'openedAt'>): Alert => ({
    status: 'open',
    lastEvaluatedAt: iso(4, now),
    siteId: 'console-acme-hq',
    ...a,
  });
  return [
    mk({ alertId: 'al-coldstore', tenantId: 'acme', ruleId: 'rule-coldstore-temp', ruleName: 'Cold store above 8 °C', kind: 'threshold', deviceId: 'env-coldstore', deviceName: 'Cold store 1', severity: 'critical', metric: 'temperature', value: 9.6, message: 'Cold store 1: temperature 9.6 °C is above 8 °C', openedAt: iso(47, now) }),
    mk({ alertId: 'al-leak', tenantId: 'acme', ruleId: 'builtin-leak', ruleName: 'Water leak detected', kind: 'leak', deviceId: 'env-warehouse', deviceName: 'Warehouse sump', severity: 'critical', metric: 'leakDetected', value: true, message: 'Warehouse sump: water leak detected', openedAt: iso(6, now) }),
    mk({ alertId: 'al-battery', tenantId: 'acme', ruleId: 'builtin-battery', ruleName: 'Battery low', kind: 'battery-low', deviceId: 'entry-front', deviceName: 'Front entrance', severity: 'warning', metric: 'batteryPercentage', value: 12, message: 'Front entrance: battery 12%', openedAt: iso(60 * 30, now) }),
    mk({ alertId: 'al-offline', tenantId: 'acme', ruleId: 'builtin-offline', ruleName: 'Sensor offline', kind: 'offline', deviceId: 'env-archive', deviceName: 'Archive room', severity: 'warning', message: 'Archive room: offline for 95 min', openedAt: iso(80, now), siteId: 'console-acme-dc' }),
    mk({ alertId: 'al-resolved-1', tenantId: 'acme', ruleId: 'rule-dock-open', ruleName: 'Dock door open > 30 min', kind: 'door-open', deviceId: 'entry-dock', deviceName: 'Loading dock door 2', severity: 'warning', message: 'Loading dock door 2: open for 42 min', openedAt: iso(60 * 26, now), status: 'resolved', resolvedAt: iso(60 * 25, now), resolvedBy: 'system' }),
    mk({ alertId: 'al-resolved-2', tenantId: 'acme', ruleId: 'builtin-offline', ruleName: 'Sensor offline', kind: 'offline', deviceId: 'motion-office', deviceName: 'Office corridor', severity: 'warning', message: 'Office corridor: offline for 16 min', openedAt: iso(60 * 50, now), status: 'resolved', resolvedAt: iso(60 * 49, now), resolvedBy: 'priya@acme-logistics.example' }),
  ];
}

export function demoEvents(now = Date.now()): EventRecord[] {
  const ev = (tenantId: string, minutesAgo: number, e: Omit<EventRecord, 'tenantId' | 'eventId' | 'ts'>): EventRecord => ({
    tenantId,
    eventId: `ev-${tenantId}-${minutesAgo}-${e.type}`,
    ts: iso(minutesAgo, now),
    ...e,
  });
  return [
    ev('acme', 4, { type: 'sync', source: 'sync', severity: 'info', title: 'Sync completed: 10 sensor(s) across 2 console(s)' }),
    ev('acme', 6, { type: 'alert', source: 'alert-engine', severity: 'critical', title: 'Alert opened: Warehouse sump: water leak detected', deviceId: 'env-warehouse', deviceName: 'Warehouse sump' }),
    ev('acme', 6, { type: 'webhook', source: 'webhook', severity: 'critical', title: 'Water leak – warehouse (sensor_leak) – Warehouse sump', deviceId: 'env-warehouse', deviceName: 'Warehouse sump', payload: { alarm: { name: 'Water leak – warehouse', triggers: [{ key: 'sensor_leak', device: 'F4E2C6AABB03' }] }, timestamp: now - 6 * 60_000 } }),
    ev('acme', 47, { type: 'alert', source: 'alert-engine', severity: 'critical', title: 'Alert opened: Cold store 1: temperature 9.6 °C is above 8 °C', deviceId: 'env-coldstore', deviceName: 'Cold store 1' }),
    ev('acme', 80, { type: 'alert', source: 'alert-engine', severity: 'warning', title: 'Alert opened: Archive room: offline for 15 min', deviceId: 'env-archive', deviceName: 'Archive room' }),
    ev('acme', 60 * 25, { type: 'alert', source: 'alert-engine', severity: 'info', title: 'Alert resolved: Loading dock door 2: open for 42 min', deviceId: 'entry-dock', deviceName: 'Loading dock door 2' }),
    ev('acme', 60 * 30, { type: 'alert', source: 'alert-engine', severity: 'warning', title: 'Alert opened: Front entrance: battery 12%', deviceId: 'entry-front', deviceName: 'Front entrance' }),
    ev('acme', 60 * 49, { type: 'alert', source: 'api', severity: 'info', title: 'Alert acknowledged: Office corridor: offline for 16 min', actor: 'priya@acme-logistics.example', deviceId: 'motion-office', deviceName: 'Office corridor' }),
    ev('acme', 60 * 72, { type: 'audit', source: 'api', title: 'Invited priya@acme-logistics.example as tenant-admin', actor: 'ops@msp.example' }),
    ev('acme', 60 * 72 + 5, { type: 'audit', source: 'api', title: 'Integration updated (API key, consoles, enabled)', actor: 'ops@msp.example' }),
    ev('acme', 60 * 24 * 120, { type: 'audit', source: 'api', title: 'Tenant "Acme Logistics" created', actor: 'ops@msp.example' }),
    ev('harbour', 2, { type: 'sync', source: 'sync', severity: 'info', title: 'Sync completed: 4 sensor(s) across 1 console(s)' }),
    ev('harbour', 60 * 24 * 45, { type: 'audit', source: 'api', title: 'Tenant "Harbour Medical Centre" created', actor: 'ops@msp.example' }),
  ];
}

export function demoUsers(now = Date.now()): TenantUser[] {
  return [
    { tenantId: 'acme', username: 'u-priya', email: 'priya@acme-logistics.example', role: 'tenant-admin', userStatus: 'CONFIRMED', enabled: true, mfaEnabled: true, invitedAt: iso(60 * 24 * 72, now), invitedBy: 'ops@msp.example' },
    { tenantId: 'acme', username: 'u-marco', email: 'marco@acme-logistics.example', role: 'tenant-viewer', userStatus: 'FORCE_CHANGE_PASSWORD', enabled: true, mfaEnabled: false, invitedAt: iso(60 * 24 * 2, now), invitedBy: 'priya@acme-logistics.example' },
    { tenantId: 'harbour', username: 'u-liam', email: 'admin@harbourmedical.example', role: 'tenant-admin', userStatus: 'CONFIRMED', enabled: true, invitedAt: iso(60 * 24 * 44, now), invitedBy: 'ops@msp.example' },
  ];
}

export function demoIntegrations(now = Date.now()): Record<string, UnifiIntegration> {
  return {
    acme: { tenantId: 'acme', enabled: true, hasApiKey: true, apiKeyLast4: '9f2c', consoleIds: ['console-acme-hq', 'console-acme-dc'], webhookToken: 'demo-webhook-token-acme', webhookUrl: 'https://api.example.com/webhooks/acme/demo-webhook-token-acme', syncIntervalMinutes: 5, lastSyncAt: iso(4, now), lastSyncStatus: 'ok', lastSyncDeviceCount: 10, updatedAt: iso(60 * 24 * 72, now), updatedBy: 'ops@msp.example' },
    harbour: { tenantId: 'harbour', enabled: true, hasApiKey: true, apiKeyLast4: '77aa', consoleIds: [], webhookToken: 'demo-webhook-token-harbour', webhookUrl: 'https://api.example.com/webhooks/harbour/demo-webhook-token-harbour', syncIntervalMinutes: 5, lastSyncAt: iso(2, now), lastSyncStatus: 'ok', lastSyncDeviceCount: 4, updatedAt: iso(60 * 24 * 44, now), updatedBy: 'ops@msp.example' },
  };
}

/** Synthetic time series with a daily cycle, so charts have something to show. */
export function generateReadings(device: Device, fromIso: string, toIso: string, stepMinutes = 5): Reading[] {
  const from = new Date(fromIso).getTime();
  const to = new Date(toIso).getTime();
  const m = device.metrics;
  const out: Reading[] = [];
  let seed = 0;
  for (const ch of device.deviceId) seed = (seed * 31 + ch.charCodeAt(0)) >>> 0;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff - 0.5;
  };
  const stopAt = device.status === 'offline' || device.status === 'dormant' ? new Date(device.lastSeenAt ?? toIso).getTime() : to;
  for (let t = from; t <= Math.min(to, stopAt); t += stepMinutes * 60_000) {
    const hours = (t / 3_600_000) % 24;
    const day = Math.sin(((hours - 6) / 24) * Math.PI * 2);
    const progress = (t - from) / Math.max(1, to - from);
    const metrics: Reading['metrics'] = {};
    if (m.temperature !== undefined) {
      const drift = device.deviceId === 'env-coldstore' ? (progress > 0.85 ? (progress - 0.85) * 40 : 0) : 0;
      metrics.temperature = Math.round((m.temperature - drift * 0 + day * 1.4 + rand() * 0.6 + (device.deviceId === 'env-coldstore' ? -5.6 + drift : 0)) * 10) / 10;
    }
    if (m.humidity !== undefined) metrics.humidity = Math.round((m.humidity - day * 3 + rand() * 1.5) * 10) / 10;
    if (m.light !== undefined) metrics.light = Math.max(0, Math.round(m.light * (0.55 + 0.45 * day) + rand() * 20));
    if (m.batteryPercentage !== undefined) metrics.batteryPercentage = Math.round(m.batteryPercentage + (1 - progress) * 0.8);
    if (m.isOpened !== undefined) metrics.isOpened = Math.abs(rand()) > 0.42;
    if (m.motionDetected !== undefined) metrics.motionDetected = Math.abs(rand()) > 0.35;
    out.push({ deviceId: device.deviceId, ts: new Date(t).toISOString(), metrics });
  }
  return out;
}

const PLATE_SEEDS: {
  tenantId: string;
  /** The plate on the vehicle, as its picture shows it. */
  raw: string;
  /** The name Protect has a known vehicle saved under, which it sends instead of the plate. */
  name?: string;
  deviceId: string;
  deviceName: string;
  siteId: string;
  minutesAgo: number;
  known: boolean;
}[] = [
  { tenantId: 'acme', raw: '1AB 2CD', name: 'Fleet ute', deviceId: 'cam-dock', deviceName: 'Loading dock', siteId: 'console-acme-hq', minutesAgo: 12, known: true },
  { tenantId: 'acme', raw: 'XY 7788', deviceId: 'cam-dock', deviceName: 'Loading dock', siteId: 'console-acme-hq', minutesAgo: 47, known: false },
  { tenantId: 'acme', raw: 'DV 5521', name: 'Delivery van', deviceId: 'cam-dock', deviceName: 'Loading dock', siteId: 'console-acme-hq', minutesAgo: 95, known: true },
  { tenantId: 'acme', raw: '1AB 2CD', name: 'Fleet ute', deviceId: 'cam-carpark', deviceName: 'Car park', siteId: 'console-acme-dc', minutesAgo: 190, known: true },
  { tenantId: 'acme', raw: 'CT-4411', deviceId: 'cam-carpark', deviceName: 'Car park', siteId: 'console-acme-dc', minutesAgo: 320, known: false },
  { tenantId: 'acme', raw: 'DV 5521', name: 'Delivery van', deviceId: 'cam-carpark', deviceName: 'Car park', siteId: 'console-acme-dc', minutesAgo: 60 * 5, known: true },
  { tenantId: 'acme', raw: '1AB 2CD', name: 'Fleet ute', deviceId: 'cam-dock', deviceName: 'Loading dock', siteId: 'console-acme-hq', minutesAgo: 60 * 26, known: true },
  { tenantId: 'acme', raw: 'QQ 1090', deviceId: 'cam-dock', deviceName: 'Loading dock', siteId: 'console-acme-hq', minutesAgo: 60 * 30, known: false },
  // The fleet ute before it went on a list, when Protect still sent its plate.
  { tenantId: 'acme', raw: '1ab2cd', deviceId: 'cam-carpark', deviceName: 'Car park', siteId: 'console-acme-dc', minutesAgo: 60 * 72, known: false },
  { tenantId: 'harbour', raw: 'PH 2244', deviceId: 'cam-dispensary', deviceName: 'Dispensary counter', siteId: 'console-harbour', minutesAgo: 90, known: false },
];

/** Reads as the webhook stores them: a known vehicle by its name, as Protect sends it. */
export function demoPlates(now = Date.now()): PlateDetection[] {
  return PLATE_SEEDS.map((p, i): PlateDetection => {
    const ts = iso(p.minutesAgo, now);
    return {
      tenantId: p.tenantId,
      detectionId: `demo-plate-${i}`,
      ts,
      receivedAt: ts,
      ...(p.name
        ? { plate: vehicleKey(p.name), rawPlate: '', vehicleName: p.name, vehicleKey: vehicleKey(p.name), plateSource: 'name' as const }
        : { plate: normalisePlate(p.raw), rawPlate: p.raw }),
      deviceId: p.deviceId,
      deviceName: p.deviceName,
      siteId: p.siteId,
      alarmName: p.known ? 'Known vehicles' : 'Unknown vehicles',
      triggerKey: p.known ? 'license_plate_known' : 'license_plate_unknown',
      known: p.known,
      field: p.name ? 'trigger.group.name' : 'trigger.value',
      // One read without a picture shows what a rule without thumbnails looks like.
      hasThumbnail: p.raw !== 'QQ 1090',
    };
  }).sort((a, b) => b.ts.localeCompare(a.ts));
}

/** The plate each read's drawn picture shows, which a known vehicle's read does not carry. */
export function demoPlatePictures(): Map<string, string> {
  return new Map(PLATE_SEEDS.map((p, i) => [`demo-plate-${i}`, p.raw]));
}

/** The fleet ute's plate is saved; the delivery van's is not, so the page offers to add it. */
export function demoNamedVehicles(now = Date.now()): NamedVehicle[] {
  return [
    {
      tenantId: 'acme',
      vehicleKey: vehicleKey('Fleet ute'),
      name: 'Fleet ute',
      plate: '1AB2CD',
      rawPlate: '1AB 2CD',
      updatedAt: iso(60 * 48, now),
      updatedBy: 'tenant@acme.example',
    },
  ];
}
